import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Input,
  Button,
  Typography,
  Space,
  Table,
  Tag,
  Empty,
  Alert,
  theme,
} from "antd";
import {
  SearchOutlined,
  RightOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { authenticatedFetch } from "@/libs/api";

const { Title, Text } = Typography;

export default function SystemSearch() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

  const isSuperadmin = localStorage.getItem("roleType") === "SUPERADMIN";
  const userPermissions = (() => {
    try { return JSON.parse(localStorage.getItem("permissions") || "[]"); }
    catch { return []; }
  })();
  const isProtectedView = !isSuperadmin && userPermissions.includes("system:protected_view") && !userPermissions.includes("system:read");

  const getValidationError = (value) => {
    const v = value.trim();
    if (!v) return null;
    // SN/NMI always contain digits; customer names typically don't
    const looksLikeSNorNMI = /\d/.test(v);
    if (looksLikeSNorNMI && v.length < 5) return "SN / NMI requires at least 5 characters";
    if (!looksLikeSNorNMI && v.length < 3) return "Customer name requires at least 3 characters";
    return null;
  };

  const handleSearch = async () => {
    const error = getValidationError(searchValue);
    if (error || !searchValue.trim()) return;

    setLoading(true);
    setSearched(true);

    try {
      const q = searchValue.trim();
      const resp = await authenticatedFetch(`${API_BASE}/api/systems/search?q=${encodeURIComponent(q)}`);

      if (resp.ok) {
        const result = await resp.json();
        if (result.success && result.data) {
          setSearchResults(
            result.data.map((sys) => ({
              id: sys.id,
              sn: sys.sn,
              nmi: sys.nmi || "—",
              group: sys.control_group_name || "Unassigned",
              customerName: sys.customer_name || "—",
              // POW-187: the SITE address for this system. Never the shared
              // customer address — one customer can own several systems.
              address:
                sys.addressline ||
                [sys.street, sys.suburb, sys.state, sys.postcode]
                  .filter(Boolean).join(" ").trim() ||
                "—",
              onboardDate: sys.onboard_success_date || sys.onboard_date || "—",
              inverterKw: sys.inverter_kw || "—",
              batteryKwh: sys.battery_kwh || "—",
            }))
          );
        } else {
          setSearchResults([]);
        }
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error("Search error:", err);
      setSearchResults([]);
    }

    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleViewDetail = (id) => {
    navigate(`/systems/${id}`);
  };

  const allColumns = [
    {
      title: "Serial Number",
      dataIndex: "sn",
      key: "sn",
      render: (sn) => <Text code style={{ fontSize: 13 }}>{sn}</Text>,
    },
    !isProtectedView && {
      title: "NMI",
      dataIndex: "nmi",
      key: "nmi",
      render: (nmi) => <Text type="secondary">{nmi}</Text>,
    },
    {
      title: "Group",
      dataIndex: "group",
      key: "group",
      render: (group) => <Tag color="blue">{group}</Tag>,
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
    },
    !isProtectedView && {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (addr) => <Text type="secondary" style={{ fontSize: 12 }}>{addr}</Text>,
    },
    {
      title: "",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<RightOutlined />}
          onClick={() => handleViewDetail(record.id)}
        >
          View
        </Button>
      ),
    },
  ].filter(Boolean);
  const columns = allColumns;

  return (
    <PageLayout active="systemsSearch">
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>Search System</Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Find a system by serial number, NMI, or customer name
        </Text>
      </div>

      {/* Search Box */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 24 }}>
        <div style={{ maxWidth: 600 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            Search Systems
          </Text>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              size="large"
              placeholder="Enter serial number, NMI, or customer name..."
              prefix={<SearchOutlined style={{ color: token.colorTextSecondary }} />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
              allowClear
              status={getValidationError(searchValue) ? "error" : ""}
            />
            <Button
              type="primary"
              size="large"
              onClick={handleSearch}
              loading={loading}
              disabled={!!getValidationError(searchValue)}
            >
              Search
            </Button>
          </Space.Compact>
          {getValidationError(searchValue) ? (
            <Text type="danger" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
              {getValidationError(searchValue)}
            </Text>
          ) : (
            <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
              SN / NMI: partial match (min 5 chars); customer name: partial match (min 3 chars)
            </Text>
          )}
        </div>
      </Card>

      {/* Search Results */}
      {searched && (
        <Card
          bordered={false}
          style={{ borderRadius: 12 }}
          title={
            <Space>
              <SearchOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontSize: 16, fontWeight: 600 }}>
                Search Results
                {searchResults.length > 0 && (
                  <Text type="secondary" style={{ fontSize: 13, fontWeight: 400, marginLeft: 8 }}>
                    ({searchResults.length} found)
                  </Text>
                )}
              </span>
            </Space>
          }
          styles={{
            header: {
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
              padding: "12px 16px",
              minHeight: 48,
            },
            body: { padding: searchResults.length > 0 ? 0 : 24 },
          }}
        >
          {searchResults.length > 0 ? (
            <Table
              columns={columns}
              dataSource={searchResults}
              rowKey="id"
              pagination={searchResults.length > 10 ? { pageSize: 10 } : false}
              size="middle"
            />
          ) : (
            <Empty
              description={
                <span>
                  No systems found for <Text code>{searchValue}</Text>
                </span>
              }
            />
          )}
        </Card>
      )}

      {/* Tips */}
      {!searched && (
        <Alert
          message="Search Tips"
          description={
            <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
              <li>Enter at least 5 characters of a serial number (SN) for a partial match</li>
              <li>Enter at least 5 characters of an NMI for a partial match</li>
              <li>Enter at least 3 characters of a customer name for a partial match</li>
              <li>You can also browse systems in the Systems Summary page</li>
            </ul>
          }
          type="info"
          showIcon
        />
      )}
    </PageLayout>
  );
}
